import React from 'react';
import { useTheme } from '@mui/material';
import { Box, Divider, Flex, Heading, Link as LinkV3, Text } from 'pounceV2';
import { PAGE_HEADER_ACTIONS_PORTAL_ID } from 'Components/PageHeader/PageHeaderV3';

interface SettingsPageHeaderV3Props {
  /** Group label rendered as small breadcrumb above the title (e.g. "General Settings"). */
  category: string;
  /** Page title (e.g. "Main Info & Preferences"). */
  title: string;
  /** Optional one-sentence description rendered below the title. */
  subheader?: string;
  /** Optional "Read more in our documentation" affordance shown on the right. */
  docsHref?: string;
}

const SettingsPageHeaderV3: React.FC<SettingsPageHeaderV3Props> = ({
  category,
  title,
  subheader,
  docsHref,
}) => {
  const {
    palette: { tokens },
  } = useTheme();

  return (
    <Box as="header" mb={8}>
      <Flex justify="space-between" align="flex-end" gap={4}>
        <Box flex={1} minWidth={0}>
          <Text
            fontSize="small"
            color={tokens.content.text.neutral[3]}
            data-tid="settings-page-header-category"
          >
            {category}
          </Text>
          <Box mt={1}>
            <Heading variant="h3" data-tid="settings-page-header-title">
              {title}
            </Heading>
          </Box>
          {subheader && (
            <Box mt={2}>
              <Text
                fontSize="small"
                color={tokens.content.text.neutral[2]}
                data-tid="settings-page-header-subheader"
              >
                {subheader}
              </Text>
            </Box>
          )}
        </Box>
        {docsHref && (
          <LinkV3
            external
            href={docsHref}
            fontSize="small"
            data-tid="settings-page-header-docs-link"
          >
            Read more in our documentation
          </LinkV3>
        )}
        {/* Portal target for `PageHeaderV3.Actions` (used by ListUsersV3,
            ListRolesV3, ListAPITokensV3, …). Shares the id with the
            top-app `HeaderV3`; `LayoutWrapper` mounts one or the other
            so the id only ever appears once. The constant lives next
            to `PageHeaderV3.Actions` so the contract is explicit. */}
        <Flex id={PAGE_HEADER_ACTIONS_PORTAL_ID} align="center" gap={2} />
      </Flex>
      {/* `margin={0}` before `mt={6}` — pounceV2 prop-order resolution
          means later props win, so margin shorthand must precede the
          specific override. Other Dividers in the codebase follow the
          same convention. */}
      <Divider margin={0} mt={6} color={tokens.stroke.neutral[5]} />
    </Box>
  );
};

export default React.memo(SettingsPageHeaderV3);
